import type { AgentAccent } from '@ralph/contracts'
import clsx from 'clsx'
import { accentTileStyle } from '@/components/marketplace/accents.ts'
import { monogram } from './agent-identity.ts'

/**
 * An agent's identity tile: its emoji, or initials on its accent.
 *
 * `aria-hidden` throughout. The tile never appears without the agent's name beside it, and
 * announcing "E B" before the name that produced it is noise for a screen reader — the same
 * reason the shell's own avatar initials are hidden.
 *
 * The three sizes are the three places the design puts an agent: 21px in a header pill beside
 * a conversation, 37px on a gallery card, 56px as the identity of a maker's draft. The radius
 * tracks the size at roughly a third of it, which is what keeps a 21px tile from looking like
 * a circle and a 56px one from looking like a photograph — a fixed radius across the family
 * reads as three unrelated shapes.
 *
 * The tile carries no border. Its depth comes from the two shadows in `accentTileStyle`, and
 * a hairline ring on top of a cast shadow makes the tile look like a sticker on the card
 * rather than an object on it.
 */
export const AgentAvatar = ({
  displayName,
  icon,
  accent,
  size = 'md',
  className
}: {
  displayName: string
  icon: string
  accent: AgentAccent
  size?: 'sm' | 'md' | 'lg'
  className?: string
}) => (
  <span
    aria-hidden
    style={accentTileStyle(accent)}
    className={clsx(
      'inline-flex shrink-0 select-none items-center justify-center',
      size === 'sm' && 'h-[21px] w-[21px] rounded-[7px] text-[10px]',
      size === 'md' && 'h-[37px] w-[37px] rounded-tile text-[17px]',
      size === 'lg' && 'h-[56px] w-[56px] rounded-[19px] text-[25px]',
      // Initials are set in the monospace grammar the rest of the instrument uses; an emoji
      // renders in its own font either way.
      'font-mono font-semibold tracking-[0.02em]',
      className
    )}
  >
    {icon || monogram(displayName)}
  </span>
)
